import { useEffect, useRef, useState } from 'react';
import { MotionConfig } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Sparkles, Disc3, ExternalLink, Music, Users, Radio } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import LastfmBadge from '@/components/LastfmBadge';
import SongstatsPanel from '@/components/SongstatsPanel';
import { useTriggerFocusReturn } from '@/lib/useTriggerFocusReturn';
import { MODAL_DIALOG_MOTION_CLASSES } from '@/lib/motion';

const KEYS = ['C', 'C♯ / D♭', 'D', 'D♯ / E♭', 'E', 'F', 'F♯ / G♭', 'G', 'G♯ / A♭', 'A', 'A♯ / B♭', 'B'];
const MODES = { 1: 'major', 0: 'minor' };

function keyLabel(features) {
  if (!features || features.key == null) return null;
  const k = KEYS[features.key] || '?';
  const m = MODES[features.mode] || '';
  return `${k} ${m}`.trim();
}

function formatTempo(t) {
  if (!t) return null;
  return `${Math.round(t)} BPM`;
}

function formatCount(n) {
  if (n == null) return null;
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function Pct({ v, label }) {
  if (v == null) return null;
  const pct = Math.round((v || 0) * 100);
  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <div className="text-[10px] uppercase font-mono text-muted-foreground">{label}</div>
      <div className="font-display text-xl font-semibold mt-0.5">{pct}%</div>
      <div className="w-full h-1 bg-secondary rounded-full mt-1.5 overflow-hidden">
        <div className="h-full bg-[hsl(var(--primary))]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function TrackDeepDive({ trackId, open, onClose, seedCard }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Ref + autoFocus prevention so the modal opens at scrollTop=0 every time
  // (Radix otherwise auto-scrolls to focus the first focusable element,
  // which can hide the track header / clip the bio for long content).
  const contentRef = useRef(null);
  // Focus round-trip — hand focus back to the button that opened us.
  const { handleCloseAutoFocus } = useTriggerFocusReturn(open);

  useEffect(() => {
    if (!open || !trackId) return;
    let active = true;
    setLoading(true);
    setData(null);
    (async () => {
      try {
        const { data: d } = await api.get(`/spotify/track/${trackId}/deep-dive`);
        if (active) setData(d);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load track deep dive');
        if (active) setData({});
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [trackId, open]);

  const track = data?.track;
  const features = data?.audio_features;
  const tadbTrack = data?.theaudiodb;
  const lf = data?.lastfm || {};
  const lfInfo = lf.info || null;
  const lfSimilar = lf.similar || [];
  const title = track?.name || seedCard?.title || 'Track deep dive';
  const artist = (track?.artists || []).map((a) => a.name).join(', ') || seedCard?.artist || '';
  const art = (track?.album?.images || [])[0]?.url || seedCard?.album_art_url;

  const musicVideo = tadbTrack?.music_video;
  const musicVideoEmbed = (() => {
    if (!musicVideo) return null;
    const m = musicVideo.match(/(?:v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{11})/);
    return m ? `https://www.youtube.com/embed/${m[1]}` : null;
  })();

  return (
    <MotionConfig reducedMotion="user">
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        ref={contentRef}
        className={`max-w-md sm:max-w-lg w-[95vw] max-h-[92vh] overflow-y-auto bg-card border-border space-y-4 pb-6 ${MODAL_DIALOG_MOTION_CLASSES}`}
        data-testid="track-deep-dive"
        onOpenAutoFocus={(e) => {
          // Prevent Radix's auto-scroll-to-focused-element behavior that was
          // clipping the top of the modal content.
          e.preventDefault();
          requestAnimationFrame(() => {
            if (contentRef.current) contentRef.current.scrollTop = 0;
          });
        }}
        onCloseAutoFocus={handleCloseAutoFocus}
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[hsl(var(--primary))]" /> Track deep dive
          </DialogTitle>
          <DialogDescription className="sr-only">Audio features and Spotify metadata</DialogDescription>
        </DialogHeader>
        <div className="flex gap-3 items-center">
          <div className="w-20 h-20 rounded-lg overflow-hidden bg-secondary grid place-items-center shrink-0">
            {art ? (
              <img src={art} alt={title} className="w-full h-full object-cover" />
            ) : (
              <Disc3 className="w-6 h-6 text-muted-foreground" />
            )}
          </div>
          <div className="min-w-0">
            <div className="font-display text-lg font-semibold truncate">{title}</div>
            <div className="text-sm text-muted-foreground truncate">{artist}</div>
            {track?.album?.name && (
              <div className="text-xs text-muted-foreground truncate">From: {track.album.name}</div>
            )}
          </div>
        </div>

        {/* Embedded Spotify preview iframe (works without auth) */}
        {trackId && (
          <iframe
            title="Spotify preview"
            src={`https://open.spotify.com/embed/track/${trackId}?utm_source=groovlens`}
            width="100%"
            height="152"
            frameBorder="0"
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            className="rounded-xl mt-1"
            data-testid="track-spotify-embed"
          />
        )}

        {/* Audio features */}
        <div className="flex flex-wrap gap-2">
          {formatTempo(features?.tempo) && (
            <Badge variant="secondary" className="text-xs font-mono">
              {formatTempo(features.tempo)}
            </Badge>
          )}
          {keyLabel(features) && (
            <Badge variant="secondary" className="text-xs font-mono">
              Key: {keyLabel(features)}
            </Badge>
          )}
          {features?.time_signature && (
            <Badge variant="secondary" className="text-xs font-mono">
              {features.time_signature}/4
            </Badge>
          )}
          {track?.popularity != null && (
            <Badge variant="outline" className="text-xs font-mono">
              ★ {track.popularity}
            </Badge>
          )}
        </div>

        {/* Songstats enrichment — Charts & Playlists + Remix badge.
            Cache-first: free for tracks any GroovLenser has looked up before.
            Renders below the audio-features badges so the "commercial signal"
            (streams / playlist reach) sits alongside the "musical DNA" (BPM/key). */}
        {seedCard?.id && (
          <SongstatsPanel
            cardId={seedCard.id}
            seedCard={seedCard}
            hasSpotifyMatch={
              !!(seedCard.spotify && (seedCard.spotify.track_id || seedCard.spotify.id))
            }
          />
        )}

        {loading && !features && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={`skeleton-${i}`} className="h-20 rounded-lg" />
            ))}
          </div>
        )}
        {features && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Pct v={features.energy} label="Energy" />
            <Pct v={features.danceability} label="Danceability" />
            <Pct v={features.valence} label="Valence" />
            <Pct v={features.acousticness} label="Acoustic" />
          </div>
        )}
        {data && !features && !loading && (
          <div className="text-xs text-muted-foreground italic">
            Spotify audio features unavailable for this track.
          </div>
        )}

        {/* Track description from TheAudioDB (preferred) or Last.fm wiki summary (fallback) */}
        {(tadbTrack?.description || lfInfo?.summary) && (
          <section>
            <h3 className="text-xs uppercase font-mono text-muted-foreground mb-1.5">About</h3>
            <div
              className="text-sm text-foreground/85 leading-relaxed line-clamp-6"
              dangerouslySetInnerHTML={{
                __html: tadbTrack?.description || lfInfo?.summary || '',
              }}
            />
          </section>
        )}

        {/* Music video from TheAudioDB */}
        {musicVideoEmbed && (
          <section data-testid="track-music-video">
            <h3 className="text-xs uppercase font-mono text-muted-foreground mb-1.5">Music video</h3>
            <div className="aspect-video w-full rounded-xl overflow-hidden bg-black">
              <iframe
                title={`${title} music video`}
                src={musicVideoEmbed}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                width="100%"
                height="100%"
                frameBorder="0"
              />
            </div>
          </section>
        )}

        {/* Last.fm stats — listeners + scrobbles + crowd tags */}
        {lfInfo && (lfInfo.listeners != null || lfInfo.playcount != null || (lfInfo.tags || []).length > 0) && (
          <section data-testid="track-lastfm-stats">
            <h3 className="text-xs uppercase font-mono text-muted-foreground mb-2 inline-flex items-center gap-1.5">
              <Radio className="w-3.5 h-3.5" /> Last.fm reach
            </h3>
            <div className="grid grid-cols-2 gap-2 mb-2">
              {lfInfo.listeners != null && (
                <div className="rounded-lg border border-border bg-background p-3" data-testid="lastfm-listeners">
                  <div className="text-[10px] uppercase font-mono text-muted-foreground inline-flex items-center gap-1">
                    <Users className="w-3 h-3" /> Listeners
                  </div>
                  <div className="font-display text-xl font-semibold mt-0.5">
                    {formatCount(lfInfo.listeners)}
                  </div>
                </div>
              )}
              {lfInfo.playcount != null && (
                <div className="rounded-lg border border-border bg-background p-3" data-testid="lastfm-playcount">
                  <div className="text-[10px] uppercase font-mono text-muted-foreground">Scrobbles</div>
                  <div className="font-display text-xl font-semibold mt-0.5">
                    {formatCount(lfInfo.playcount)}
                  </div>
                </div>
              )}
            </div>
            {(lfInfo.tags || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {lfInfo.tags.slice(0, 8).map((t) => (
                  <Badge
                    key={`lf-tag-${t.name}`}
                    variant="outline"
                    className="text-[10px] font-mono lowercase"
                  >
                    {t.name}
                  </Badge>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Similar tracks (Last.fm) */}
        {lfSimilar.length > 0 && (
          <section data-testid="track-lastfm-similar">
            <h3 className="text-xs uppercase font-mono text-muted-foreground mb-2">
              Listeners also play
            </h3>
            <ul className="space-y-1.5">
              {lfSimilar.slice(0, 6).map((s, idx) => (
                <li
                  key={`lf-sim-${s.url || idx}`}
                  className="flex items-center gap-3 p-2 rounded-lg border border-border bg-background"
                >
                  <span className="w-5 text-center text-xs font-mono text-muted-foreground">
                    {idx + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{s.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {s.artist_name}
                    </div>
                  </div>
                  {s.match != null && (
                    <Badge variant="secondary" className="text-[10px] font-mono shrink-0">
                      {Math.round(s.match * 100)}% match
                    </Badge>
                  )}
                  {s.url && (
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-foreground shrink-0"
                      aria-label="Open on Last.fm"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {track?.external_url && (
          <a
            href={track.external_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-[#1DB954] hover:underline"
          >
            <Music className="w-4 h-4" /> Open in Spotify <ExternalLink className="w-3 h-3" />
          </a>
        )}

        {/* Attribution footer — Last.fm requests data attribution */}
        {(lfInfo || lfSimilar.length > 0) && (
          <div className="pt-2 mt-1 border-t border-border/50 flex items-center justify-between gap-2">
            <LastfmBadge variant="inline" href={lfInfo?.url || 'https://www.last.fm/'} />
            {lfInfo?.url && (
              <a
                href={lfInfo.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] font-mono text-muted-foreground/70 hover:text-foreground transition-colors inline-flex items-center gap-1"
                data-testid="lastfm-track-external"
              >
                View on Last.fm <ExternalLink className="w-2.5 h-2.5" />
              </a>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
    </MotionConfig>
  );
}
